// src/services/auth.js
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserSessionPersistence, 
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Always prompt user to select an account
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Register with email and password
export const registerWithEmailAndPassword = async (email, password, userType, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update profile with display name
    await updateProfile(user, { displayName });
    
    // Create user document in Firestore
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email,
      displayName,
      userType,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp()
    });
    
    // Create specific user type document
    await setDoc(doc(db, userType, user.uid), {
      uid: user.uid,
      email,
      displayName,
      createdAt: serverTimestamp(),
      // Initial empty profile based on user type
      profile: getInitialProfile(userType)
    });
    
    return user;
  } catch (error) {
    throw error;
  }
};

// Sign in with email and password
export const loginWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update last login
    await updateUserLastLogin(user.uid);
    
    return userCredential; // Return the full userCredential object
  } catch (error) {
    throw error;
  }
};

// Sign in with Google
// First, try using popup as normal since it's simpler
export const signInWithGoogle = async () => {
  try {
    // Try using popup first
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Check if user exists
    const userDoc = await getDoc(doc(db, "users", user.uid));
    
    if (!userDoc.exists()) {
      // New user - redirect to user type selection
      return { user, isNewUser: true };
    } else {
      // Existing user - update last login
      await updateUserLastLogin(user.uid);
      return { user, isNewUser: false, userType: userDoc.data().userType };
    }
  } catch (error) {
    // If we get a popup blocked error or COOP error, fall back to redirect
    if (error.code === 'auth/popup-blocked' || 
        error.message.includes('Cross-Origin-Opener-Policy')) {
      console.log('Popup blocked or COOP error, falling back to redirect method');
      return signInWithGoogleRedirect();
    }
    throw error;
  }
};

// Fallback method using redirect instead of popup
export const signInWithGoogleRedirect = async () => {
  try {
    // Set persistence to SESSION to maintain login across redirects
    await setPersistence(auth, browserSessionPersistence);
    
    // Redirect to Google sign-in
    await signInWithRedirect(auth, googleProvider);
    
    // This function will redirect to Google, so no return value
    // The result will be handled by getGoogleRedirectResult
    return { redirecting: true };
  } catch (error) {
    console.error('Error with Google redirect sign-in:', error);
    throw error;
  }
};

// Get the result of the Google sign-in redirect
export const getGoogleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    
    // If there's no result, we're not in a redirect callback
    if (!result) {
      return null;
    }
    
    const user = result.user;
    
    // Check if user exists
    const userDoc = await getDoc(doc(db, "users", user.uid));
    
    if (!userDoc.exists()) {
      // New user - will need to select user type
      return { user, isNewUser: true };
    } else {
      // Existing user - update last login
      await updateUserLastLogin(user.uid);
      return { user, isNewUser: false, userType: userDoc.data().userType };
    }
  } catch (error) {
    console.error('Error handling Google redirect result:', error);
    throw error;
  }
};

// Complete Google signup with user type
export const completeGoogleSignup = async (uid, userType) => {
  try {
    const user = auth.currentUser;
    
    // Create user document in Firestore
    await setDoc(doc(db, "users", uid), {
      uid,
      email: user.email,
      displayName: user.displayName,
      userType,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      photoURL: user.photoURL || null
    });
    
    // Create specific user type document
    await setDoc(doc(db, userType, uid), {
      uid,
      email: user.email,
      displayName: user.displayName,
      createdAt: serverTimestamp(),
      photoURL: user.photoURL || null,
      // Initial empty profile based on user type
      profile: getInitialProfile(userType)
    });
    
    return { user, userType };
  } catch (error) {
    throw error;
  }
};

// Sign out
export const logOut = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    throw error;
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    throw error;
  }
};

// Update user's last login timestamp
const updateUserLastLogin = async (uid) => {
  try {
    await setDoc(doc(db, "users", uid), {
      lastLogin: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error("Error updating last login:", error);
  }
};

// Ensure user type document exists and create if missing
export const ensureUserTypeDocumentExists = async (userType, uid) => {
  try {
    const userTypeDocRef = doc(db, userType, uid);
    const userTypeDoc = await getDoc(userTypeDocRef);
    
    if (!userTypeDoc.exists()) {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Create the missing document with initial profile
      await setDoc(userTypeDocRef, {
        uid,
        email: user.email,
        displayName: user.displayName || "",
        createdAt: serverTimestamp(),
        photoURL: user.photoURL || null,
        profile: getInitialProfile(userType)
      });
      
      console.log(`Created missing ${userType} document for user ${uid}`);
    }
    
    return true;
  } catch (error) {
    console.error(`Error ensuring ${userType} document exists:`, error);
    throw error;
  }
};

// Generate initial profile structure based on user type
const getInitialProfile = (userType) => {
  switch (userType) {
    case "providers":
      return {
        type: "",  // "center" or "home"
        licensed: false,
        programs: [],
        foodMenu: [],
        ageGroups: [],
        maxCapacity: {
          infant: 0,
          toddler: 0,
          preschool: 0,
          schoolAge: 0
        },
        location: null,
        address: "",
        screenTime: false,
        activities: [],
        education: "",
        certifications: [],
        fees: {
          hourly: null,
          daily: null,
          monthly: null
        },
        contactPreference: "",
        phone: "",
        hoursOfOperation: {
          monday: { open: "", close: "" },
          tuesday: { open: "", close: "" },
          wednesday: { open: "", close: "" },
          thursday: { open: "", close: "" },
          friday: { open: "", close: "" },
          saturday: { open: "", close: "" },
          sunday: { open: "", close: "" }
        },
        languages: []
      };
    case "parents":
      return {
        children: [],
        activityPreferences: [],
        serviceType: "",  // "nanny", "provider", or "both"
        location: null,
        searchRadius: 10,  // Default 10km
        languages: [],
        phone: "",
        address: "",
        email: "",
        displayName: ""
      };
    case "jobseekers":
      return {
        type: "",  // "nanny" or "educator"
        experience: "",
        education: "",
        certifications: [],
        criminalRecordCheck: false,
        recommendations: [],
        availability: {
          monday: { start: '', end: '' },
          tuesday: { start: '', end: '' },
          wednesday: { start: '', end: '' },
          thursday: { start: '', end: '' },
          friday: { start: '', end: '' },
          saturday: { start: '', end: '' },
          sunday: { start: '', end: '' }
        },
        languages: [],
        hourlyRate: null,
        location: null,
        phone: "",
        address: "",
        email: "",
        displayName: ""
      };
    default:
      return {};
  }
};